import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Save, Download, FileText, File,
  User, Users, ClipboardList, Briefcase, FileCheck, Plus, Trash2, CheckCircle
} from 'lucide-react';
import FieldRow from '../components/FieldRow';

const STEPS = [
  { id: 1, title: "Personal Details", icon: User },
  { id: 2, title: "Family Details", icon: Users },
  { id: 3, title: "Assessment & Counselling", icon: ClipboardList },
  { id: 4, title: "Micro Business Plan", icon: Briefcase },
  { id: 5, title: "Declaration", icon: FileCheck },
  { id: 6, title: "Review & Export", icon: Download },
];

export default function SHNDFormPage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [exporting, setExporting] = useState('');

  // Form data
  const [personalDetails, setPersonalDetails] = useState({
    name: '', its: '', age: '', mauze_jamiat: '', contact_number: '',
    email: '', residential_address: '', present_occupation: '',
    occupation_address: '', other_info: ''
  });

  const [familyDetails, setFamilyDetails] = useState({
    family_members: [{ name: '', age_relation: '', education_occupation: '', annual_income: '' }],
    family_structure: '', other_details: '',
    wellbeing: { housing: '', education: '', health: '', deeni: '', ziyarat: '' },
    income_expense: {
      income_business_m: '', income_business_y: '', income_salary_m: '', income_salary_y: '',
      income_home_industry_m: '', income_home_industry_y: '', income_others_m: '', income_others_y: '',
      total_income_monthly: '', total_income_yearly: '',
      exp_food_m: '', exp_food_y: '', exp_housing_m: '', exp_housing_y: '',
      exp_health_m: '', exp_health_y: '', exp_transport_m: '', exp_transport_y: '',
      exp_education_m: '', exp_education_y: '', exp_deeni_m: '', exp_deeni_y: '',
      exp_essentials_m: '', exp_essentials_y: '', exp_nonessentials_m: '', exp_nonessentials_y: '',
      exp_others_m: '', exp_others_y: '', total_expense_monthly: '', total_expense_yearly: '',
      surplus_monthly: '', surplus_yearly: '', borrowing_m: '', borrowing_y: ''
    },
    assets_liabilities: {
      asset_residential: '', asset_shop: '', asset_machinery: '', asset_stock: '',
      asset_goods_credit: '', asset_others: '',
      liability_borrowing: '', liability_goods_credit: '', liability_others: ''
    }
  });

  const [assessment, setAssessment] = useState({
    education: '', past_experience: '', present_business: '', family_business: '',
    skills: '', counsellor_assessment: '',
    products: [{ name: '', unit: '', cost_price: '' }],
    trademark: '', online_presence: '', digital_marketing: '', store_location: '',
    supplier_details: '', customer_details: '', major_expenses: '', business_assessment: '',
    support_areas: {}
  });

  const [businessPlan, setBusinessPlan] = useState({
    financial_assistance: [{ timeline: '', purpose: '', amount: '', support_doc: '' }],
    self_funding: '', rahen_available: '',
    repayment_schedule: {},
    economic_growth: {}
  });

  const [declaration, setDeclaration] = useState({
    applicant_name: '', counsellor_name: '', tr_member_name: '', comments: ''
  });

  useEffect(() => {
    loadApplication();
  }, [appId]);

  const loadApplication = async () => {
    try {
      const res = await api.get(`/shnd/${appId}`);
      const d = res.data;
      setCurrentStep(d.current_step || 1);
      if (d.personal_details && Object.keys(d.personal_details).length > 0) setPersonalDetails(prev => ({ ...prev, ...d.personal_details }));
      if (d.family_details && Object.keys(d.family_details).length > 0) {
        setFamilyDetails(prev => {
          const merged = { ...prev };
          if (d.family_details.family_members?.length > 0) merged.family_members = d.family_details.family_members;
          if (d.family_details.family_structure) merged.family_structure = d.family_details.family_structure;
          if (d.family_details.other_details) merged.other_details = d.family_details.other_details;
          if (d.family_details.wellbeing) merged.wellbeing = { ...prev.wellbeing, ...d.family_details.wellbeing };
          if (d.family_details.income_expense) merged.income_expense = { ...prev.income_expense, ...d.family_details.income_expense };
          if (d.family_details.assets_liabilities) merged.assets_liabilities = { ...prev.assets_liabilities, ...d.family_details.assets_liabilities };
          return merged;
        });
      }
      if (d.assessment && Object.keys(d.assessment).length > 0) {
        setAssessment(prev => {
          const merged = { ...prev, ...d.assessment };
          if (d.assessment.products?.length > 0) merged.products = d.assessment.products;
          if (d.assessment.support_areas) merged.support_areas = d.assessment.support_areas;
          return merged;
        });
      }
      if (d.business_plan && Object.keys(d.business_plan).length > 0) {
        setBusinessPlan(prev => {
          const merged = { ...prev, ...d.business_plan };
          if (d.business_plan.financial_assistance?.length > 0) merged.financial_assistance = d.business_plan.financial_assistance;
          return merged;
        });
      }
      if (d.declaration && Object.keys(d.declaration).length > 0) setDeclaration(prev => ({ ...prev, ...d.declaration }));
    } catch (err) {
      toast.error('Failed to load application');
      navigate('/shnd');
    } finally {
      setLoading(false);
    }
  };

  const saveProgress = useCallback(async (step, status) => {
    setSaving(true);
    try {
      await api.put(`/shnd/${appId}`, {
        current_step: step || currentStep,
        status: status || 'draft',
        personal_details: personalDetails,
        family_details: familyDetails,
        assessment: assessment,
        business_plan: businessPlan,
        declaration: declaration
      });
      toast.success('Progress saved');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  }, [appId, currentStep, personalDetails, familyDetails, assessment, businessPlan, declaration, api]);

  const goNext = () => {
    const next = Math.min(currentStep + 1, 6);
    setCurrentStep(next);
    saveProgress(next);
  };

  const goPrev = () => {
    const prev = Math.max(currentStep - 1, 1);
    setCurrentStep(prev);
  };

  const handleComplete = async () => {
    await saveProgress(6, 'completed');
    toast.success('Application completed!');
  };

  const handleExport = async (format, type) => {
    setExporting(`${format}-${type}`);
    try {
      const res = await api.get(`/shnd/${appId}/export?format=${format}&type=${type}`, { responseType: 'blob' });
      const ext = format === 'word' ? 'docx' : 'pdf';
      const mime = format === 'word'
        ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        : 'application/pdf';
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `SHND_${personalDetails.name?.replace(/\s+/g, '_') || 'Application'}_${type}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${type === 'full' ? 'Full' : 'Summary'} ${format.toUpperCase()} exported`);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting('');
    }
  };

  // Helpers
  const updatePD = (key, val) => setPersonalDetails(p => ({ ...p, [key]: val }));
  const updateWellbeing = (key, val) => setFamilyDetails(p => ({ ...p, wellbeing: { ...p.wellbeing, [key]: val } }));
  const updateIE = (key, val) => setFamilyDetails(p => ({ ...p, income_expense: { ...p.income_expense, [key]: val } }));
  const updateAL = (key, val) => setFamilyDetails(p => ({ ...p, assets_liabilities: { ...p.assets_liabilities, [key]: val } }));
  const updateAssess = (key, val) => setAssessment(p => ({ ...p, [key]: val }));
  const updateBP = (key, val) => setBusinessPlan(p => ({ ...p, [key]: val }));
  const updateRepay = (key, val) => setBusinessPlan(p => ({ ...p, repayment_schedule: { ...p.repayment_schedule, [key]: val } }));
  const updateGrowth = (key, val) => setBusinessPlan(p => ({ ...p, economic_growth: { ...p.economic_growth, [key]: val } }));
  const updateDecl = (key, val) => setDeclaration(p => ({ ...p, [key]: val }));

  const addFamilyMember = () => setFamilyDetails(p => ({ ...p, family_members: [...p.family_members, { name: '', age_relation: '', education_occupation: '', annual_income: '' }] }));
  const removeFamilyMember = (i) => setFamilyDetails(p => ({ ...p, family_members: p.family_members.filter((_, idx) => idx !== i) }));
  const updateFamilyMember = (i, key, val) => setFamilyDetails(p => ({ ...p, family_members: p.family_members.map((m, idx) => idx === i ? { ...m, [key]: val } : m) }));

  const addProduct = () => setAssessment(p => ({ ...p, products: [...p.products, { name: '', unit: '', cost_price: '' }] }));
  const removeProduct = (i) => setAssessment(p => ({ ...p, products: p.products.filter((_, idx) => idx !== i) }));
  const updateProduct = (i, key, val) => setAssessment(p => ({ ...p, products: p.products.map((pr, idx) => idx === i ? { ...pr, [key]: val } : pr) }));

  const addFA = () => setBusinessPlan(p => ({ ...p, financial_assistance: [...p.financial_assistance, { timeline: '', purpose: '', amount: '', support_doc: '' }] }));
  const removeFA = (i) => setBusinessPlan(p => ({ ...p, financial_assistance: p.financial_assistance.filter((_, idx) => idx !== i) }));
  const updateFA = (i, key, val) => setBusinessPlan(p => ({ ...p, financial_assistance: p.financial_assistance.map((fa, idx) => idx === i ? { ...fa, [key]: val } : fa) }));

  const toggleSupport = (key) => setAssessment(p => ({ ...p, support_areas: { ...p.support_areas, [key]: !p.support_areas[key] } }));

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-slate-800 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="shnd-form-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/shnd')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">SHND Application</h1>
          <p className="text-xs text-slate-500">{appId}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => saveProgress()} disabled={saving} data-testid="save-btn">
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* Step indicators */}
      <div className="flex gap-1 items-center overflow-x-auto pb-2">
        {STEPS.map((step) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isDone = currentStep > step.id;
          return (
            <button
              key={step.id}
              onClick={() => { setCurrentStep(step.id); }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                isActive ? 'bg-slate-900 text-white shadow-lg' :
                isDone ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                'bg-slate-50 text-slate-400 border border-slate-200'
              }`}
              data-testid={`step-${step.id}`}
            >
              {isDone ? <CheckCircle className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{step.title}</span>
              <span className="sm:hidden">{step.id}</span>
            </button>
          );
        })}
      </div>

      {/* Step Content */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">{STEPS[currentStep - 1]?.title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* STEP 1: Personal Details */}
          {currentStep === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldRow label="Full Name" required value={personalDetails.name} onChange={v => updatePD('name', v)} placeholder="Applicant's full name" testId="shnd-name" />
              <FieldRow label="ITS Number" value={personalDetails.its} onChange={v => updatePD('its', v)} placeholder="8-digit ITS" validation="its" testId="shnd-its" />
              <FieldRow label="Age" value={personalDetails.age} onChange={v => updatePD('age', v)} placeholder="Age" validation="age" testId="shnd-age" />
              <FieldRow label="Mauze & Jamiat" value={personalDetails.mauze_jamiat} onChange={v => updatePD('mauze_jamiat', v)} placeholder="Mauze and Jamiat" testId="shnd-mauze" />
              <FieldRow label="Contact Number" value={personalDetails.contact_number} onChange={v => updatePD('contact_number', v)} placeholder="10-digit mobile" validation="mobile" testId="shnd-contact" />
              <FieldRow label="Email ID" value={personalDetails.email} onChange={v => updatePD('email', v)} placeholder="name@example.com" validation="email" testId="shnd-email" />
              <div className="md:col-span-2">
                <FieldRow label="Residential Address" value={personalDetails.residential_address} onChange={v => updatePD('residential_address', v)} placeholder="Full residential address" type="textarea" />
              </div>
              <FieldRow label="Present Occupation" value={personalDetails.present_occupation} onChange={v => updatePD('present_occupation', v)} placeholder="Current occupation" />
              <FieldRow label="Occupation Address" value={personalDetails.occupation_address} onChange={v => updatePD('occupation_address', v)} placeholder="Workplace address" />
              <div className="md:col-span-2">
                <FieldRow label="Other Relevant Information" value={personalDetails.other_info} onChange={v => updatePD('other_info', v)} type="textarea" placeholder="Any other details" />
              </div>
            </div>
          )}

          {/* STEP 2: Family Details */}
          {currentStep === 2 && (
            <div className="space-y-8">
              {/* Family Members */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-800">Family Members</h3>
                  <Button variant="outline" size="sm" onClick={addFamilyMember} data-testid="add-family-member">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Member
                  </Button>
                </div>
                <div className="space-y-3">
                  {familyDetails.family_members.map((m, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end p-3 bg-slate-50 rounded-lg">
                      <div className="col-span-12 sm:col-span-3">
                        <Label className="text-xs text-slate-500">Name</Label>
                        <Input value={m.name} onChange={e => updateFamilyMember(i, 'name', e.target.value)} placeholder="Name" className="text-sm" />
                      </div>
                      <div className="col-span-6 sm:col-span-3">
                        <Label className="text-xs text-slate-500">Age & Relation</Label>
                        <Input value={m.age_relation} onChange={e => updateFamilyMember(i, 'age_relation', e.target.value)} placeholder="25/Wife" className="text-sm" />
                      </div>
                      <div className="col-span-6 sm:col-span-3">
                        <Label className="text-xs text-slate-500">Education/Occupation</Label>
                        <Input value={m.education_occupation} onChange={e => updateFamilyMember(i, 'education_occupation', e.target.value)} placeholder="Education" className="text-sm" />
                      </div>
                      <div className="col-span-10 sm:col-span-2">
                        <Label className="text-xs text-slate-500">Annual Income</Label>
                        <Input value={m.annual_income} onChange={e => updateFamilyMember(i, 'annual_income', e.target.value)} placeholder="Amount" className="text-sm" />
                      </div>
                      <div className="col-span-2 sm:col-span-1 flex justify-end">
                        {familyDetails.family_members.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => removeFamilyMember(i)} className="text-red-500 h-9 w-9 p-0">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldRow label="Family Structure" value={familyDetails.family_structure} onChange={v => setFamilyDetails(p => ({ ...p, family_structure: v }))} placeholder="Nuclear/Joint/Extended" />
                <FieldRow label="Other Details" value={familyDetails.other_details} onChange={v => setFamilyDetails(p => ({ ...p, other_details: v }))} placeholder="Any other family info" />
              </div>

              {/* Wellbeing */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Present Family Wellbeing / Lifestyle</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FieldRow label="Housing" value={familyDetails.wellbeing.housing} onChange={v => updateWellbeing('housing', v)} placeholder="Housing standard" />
                  <FieldRow label="Education" value={familyDetails.wellbeing.education} onChange={v => updateWellbeing('education', v)} placeholder="Education standard" />
                  <FieldRow label="Health" value={familyDetails.wellbeing.health} onChange={v => updateWellbeing('health', v)} placeholder="Health standard" />
                  <FieldRow label="Deeni" value={familyDetails.wellbeing.deeni} onChange={v => updateWellbeing('deeni', v)} placeholder="Deeni standard" />
                  <FieldRow label="Ziyarat / Travel / Recreation" value={familyDetails.wellbeing.ziyarat} onChange={v => updateWellbeing('ziyarat', v)} placeholder="Travel/recreation" />
                </div>
              </div>

              {/* Income / Expense */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Household Income and Expense</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="text-left p-2 font-medium text-slate-700">Category</th>
                        <th className="text-left p-2 font-medium text-slate-700 w-36">Monthly</th>
                        <th className="text-left p-2 font-medium text-slate-700 w-36">Yearly</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td colSpan="3" className="p-2 font-semibold text-slate-800 bg-blue-50">INCOME</td></tr>
                      {[['Business', 'income_business'], ['Salary', 'income_salary'], ['Home Industry', 'income_home_industry'], ['Others', 'income_others']].map(([label, key]) => (
                        <tr key={key} className="border-b border-slate-100">
                          <td className="p-2 text-slate-600">{label}</td>
                          <td className="p-1"><Input value={familyDetails.income_expense[key+'_m'] || ''} onChange={e => updateIE(key+'_m', e.target.value)} className="text-sm h-8" /></td>
                          <td className="p-1"><Input value={familyDetails.income_expense[key+'_y'] || ''} onChange={e => updateIE(key+'_y', e.target.value)} className="text-sm h-8" /></td>
                        </tr>
                      ))}
                      <tr className="bg-emerald-50 font-semibold">
                        <td className="p-2 text-emerald-800">Total Family Income</td>
                        <td className="p-1"><Input value={familyDetails.income_expense.total_income_monthly || ''} onChange={e => updateIE('total_income_monthly', e.target.value)} className="text-sm h-8 font-semibold" /></td>
                        <td className="p-1"><Input value={familyDetails.income_expense.total_income_yearly || ''} onChange={e => updateIE('total_income_yearly', e.target.value)} className="text-sm h-8 font-semibold" /></td>
                      </tr>
                      <tr><td colSpan="3" className="p-2 font-semibold text-slate-800 bg-red-50">EXPENSES</td></tr>
                      {[['Food: Groceries plus others', 'exp_food'], ['Housing: Rent, Maintenance, Electricity', 'exp_housing'], ['Health: Doctor, Medicine', 'exp_health'], ['Local Transport', 'exp_transport'], ['Education: Fees, Books, Tuitions', 'exp_education'], ['Deeni: Sabeel, Wajebaat, Niyaaz', 'exp_deeni'], ['Essentials: Clothes, Personal Care', 'exp_essentials'], ['Non essentials: Recreational', 'exp_nonessentials'], ['Others', 'exp_others']].map(([label, key]) => (
                        <tr key={key} className="border-b border-slate-100">
                          <td className="p-2 text-slate-600">{label}</td>
                          <td className="p-1"><Input value={familyDetails.income_expense[key+'_m'] || ''} onChange={e => updateIE(key+'_m', e.target.value)} className="text-sm h-8" /></td>
                          <td className="p-1"><Input value={familyDetails.income_expense[key+'_y'] || ''} onChange={e => updateIE(key+'_y', e.target.value)} className="text-sm h-8" /></td>
                        </tr>
                      ))}
                      <tr className="bg-red-50 font-semibold">
                        <td className="p-2 text-red-800">Total Family Expenses</td>
                        <td className="p-1"><Input value={familyDetails.income_expense.total_expense_monthly || ''} onChange={e => updateIE('total_expense_monthly', e.target.value)} className="text-sm h-8 font-semibold" /></td>
                        <td className="p-1"><Input value={familyDetails.income_expense.total_expense_yearly || ''} onChange={e => updateIE('total_expense_yearly', e.target.value)} className="text-sm h-8 font-semibold" /></td>
                      </tr>
                      <tr className="bg-amber-50 font-semibold">
                        <td className="p-2 text-amber-800">Surplus / Deficit</td>
                        <td className="p-1"><Input value={familyDetails.income_expense.surplus_monthly || ''} onChange={e => updateIE('surplus_monthly', e.target.value)} className="text-sm h-8 font-semibold" /></td>
                        <td className="p-1"><Input value={familyDetails.income_expense.surplus_yearly || ''} onChange={e => updateIE('surplus_yearly', e.target.value)} className="text-sm h-8 font-semibold" /></td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="p-2 text-slate-600">Borrowing / Qardan</td>
                        <td className="p-1"><Input value={familyDetails.income_expense.borrowing_m || ''} onChange={e => updateIE('borrowing_m', e.target.value)} className="text-sm h-8" /></td>
                        <td className="p-1"><Input value={familyDetails.income_expense.borrowing_y || ''} onChange={e => updateIE('borrowing_y', e.target.value)} className="text-sm h-8" /></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Assets & Liabilities */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Present Assets and Liabilities</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-blue-700">ASSETS</h4>
                    {[['Residential', 'asset_residential'], ['Shop / Godown / Land', 'asset_shop'], ['Machinery / Vehicle', 'asset_machinery'], ['Stock / Raw material', 'asset_stock'], ['Goods sold on credit', 'asset_goods_credit'], ['Others', 'asset_others']].map(([label, key]) => (
                      <FieldRow key={key} label={label} value={familyDetails.assets_liabilities[key]} onChange={v => updateAL(key, v)} placeholder="Description & Amount" />
                    ))}
                  </div>
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-red-700">LIABILITIES</h4>
                    {[['Borrowing / Qardan', 'liability_borrowing'], ['Goods taken on credit', 'liability_goods_credit'], ['Others', 'liability_others']].map(([label, key]) => (
                      <FieldRow key={key} label={label} value={familyDetails.assets_liabilities[key]} onChange={v => updateAL(key, v)} placeholder="Description" />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Assessment */}
          {currentStep === 3 && (
            <div className="space-y-8">
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Applicant's Background</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FieldRow label="Education" value={assessment.education} onChange={v => updateAssess('education', v)} placeholder="Education details" />
                  <FieldRow label="Past Work Experience" value={assessment.past_experience} onChange={v => updateAssess('past_experience', v)} placeholder="Past experience" />
                  <FieldRow label="Present Business" value={assessment.present_business} onChange={v => updateAssess('present_business', v)} placeholder="Current business" />
                  <FieldRow label="Family Business" value={assessment.family_business} onChange={v => updateAssess('family_business', v)} placeholder="Family business details" />
                  <div className="md:col-span-2">
                    <FieldRow label="Skills and Knowledge" value={assessment.skills} onChange={v => updateAssess('skills', v)} type="textarea" placeholder="Relevant skills" />
                  </div>
                </div>
                <div className="mt-4">
                  <FieldRow label="Counsellor's Assessment" value={assessment.counsellor_assessment} onChange={v => updateAssess('counsellor_assessment', v)} type="textarea" placeholder="Counsellor's assessment of applicant's background" />
                </div>
              </div>

              {/* Products */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-800">Products / Services</h3>
                  <Button variant="outline" size="sm" onClick={addProduct} data-testid="add-product">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add
                  </Button>
                </div>
                {assessment.products.map((pr, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end mb-2 p-3 bg-slate-50 rounded-lg">
                    <div className="col-span-5">
                      <Label className="text-xs text-slate-500">Product / Service</Label>
                      <Input value={pr.name} onChange={e => updateProduct(i, 'name', e.target.value)} placeholder="Name" className="text-sm" />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs text-slate-500">Unit</Label>
                      <Input value={pr.unit} onChange={e => updateProduct(i, 'unit', e.target.value)} placeholder="Unit" className="text-sm" />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs text-slate-500">Cost Price</Label>
                      <Input value={pr.cost_price} onChange={e => updateProduct(i, 'cost_price', e.target.value)} placeholder="Cost" className="text-sm" />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      {assessment.products.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeProduct(i)} className="text-red-500 h-9 w-9 p-0">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Business details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldRow label="Trade Mark" value={assessment.trademark} onChange={v => updateAssess('trademark', v)} placeholder="Trade mark details" />
                <FieldRow label="Online Presence" value={assessment.online_presence} onChange={v => updateAssess('online_presence', v)} placeholder="Website, social media" />
                <FieldRow label="Digital / Social Media Marketing" value={assessment.digital_marketing} onChange={v => updateAssess('digital_marketing', v)} placeholder="Marketing channels" />
                <FieldRow label="Physical Store Location" value={assessment.store_location} onChange={v => updateAssess('store_location', v)} placeholder="Store address" />
                <div className="md:col-span-2">
                  <FieldRow label="Supplier & Credit Details" value={assessment.supplier_details} onChange={v => updateAssess('supplier_details', v)} type="textarea" placeholder="Supplier information" />
                </div>
                <div className="md:col-span-2">
                  <FieldRow label="Customers & Competitors" value={assessment.customer_details} onChange={v => updateAssess('customer_details', v)} type="textarea" placeholder="Customer base and competition" />
                </div>
                <div className="md:col-span-2">
                  <FieldRow label="Major Expenses & Overheads" value={assessment.major_expenses} onChange={v => updateAssess('major_expenses', v)} type="textarea" placeholder="Major recurring expenses" />
                </div>
              </div>

              <FieldRow label="Counsellor's Assessment of Proposed Business" value={assessment.business_assessment} onChange={v => updateAssess('business_assessment', v)} type="textarea" placeholder="Assessment of the business proposal" />

              {/* Support areas */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Support Areas Needed</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    ['industry_product_knowledge', 'Industry / Product Knowledge'],
                    ['sourcing_support', 'Sourcing Support'],
                    ['sales_market_access', 'Sales / Market Access'],
                    ['internship_skill_development', 'Internship / Skill Development'],
                    ['mentoring_and_handholding', 'Mentoring & Handholding'],
                    ['book_keeping', 'Book keeping'],
                  ].map(([key, label]) => (
                    <div key={key} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50">
                      <Checkbox id={`support-${key}`} checked={!!assessment.support_areas[key]} onCheckedChange={() => toggleSupport(key)} />
                      <Label htmlFor={`support-${key}`} className="text-sm cursor-pointer">{label}</Label>
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <FieldRow label="Others (specify)" value={assessment.support_areas.others_text || ''} onChange={v => setAssessment(p => ({ ...p, support_areas: { ...p.support_areas, others_text: v } }))} placeholder="Other support needed" />
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: Business Plan */}
          {currentStep === 4 && (
            <div className="space-y-8">
              {/* Financial Assistance */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-800">Financial Assistance Required</h3>
                  <Button variant="outline" size="sm" onClick={addFA} data-testid="add-financial-assistance">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add
                  </Button>
                </div>
                {businessPlan.financial_assistance.map((fa, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end mb-2 p-3 bg-slate-50 rounded-lg">
                    <div className="col-span-3">
                      <Label className="text-xs text-slate-500">Timeline</Label>
                      <Input value={fa.timeline} onChange={e => updateFA(i, 'timeline', e.target.value)} placeholder="When" className="text-sm" />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs text-slate-500">Purpose</Label>
                      <Input value={fa.purpose} onChange={e => updateFA(i, 'purpose', e.target.value)} placeholder="Purpose" className="text-sm" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-slate-500">Amount</Label>
                      <Input value={fa.amount} onChange={e => updateFA(i, 'amount', e.target.value)} placeholder="Amount" className="text-sm" />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs text-slate-500">Support Document</Label>
                      <Input value={fa.support_doc} onChange={e => updateFA(i, 'support_doc', e.target.value)} placeholder="Document" className="text-sm" />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      {businessPlan.financial_assistance.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeFA(i)} className="text-red-500 h-9 w-9 p-0">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldRow label="Self-funding Available" value={businessPlan.self_funding} onChange={v => updateBP('self_funding', v)} placeholder="Amount available" />
                <FieldRow label="Rahen Available" value={businessPlan.rahen_available} onChange={v => updateBP('rahen_available', v)} placeholder="Rahen details" />
              </div>

              {/* Repayment Schedule */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Yearly Repayment Schedule</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="p-2 text-left font-medium text-slate-700 w-24"></th>
                        {['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'].map(yr => (
                          <th key={yr} className="p-2 text-left font-medium text-slate-700">{yr}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[['QH 1', 'qh1'], ['QH 2', 'qh2'], ['NA', 'na'], ['TOTAL', 'total']].map(([label, prefix]) => (
                        <tr key={prefix} className={prefix === 'total' ? 'bg-blue-50 font-semibold' : 'border-b border-slate-100'}>
                          <td className="p-2 text-slate-700">{label}</td>
                          {[1, 2, 3, 4, 5].map(yr => (
                            <td key={yr} className="p-1">
                              <Input
                                value={businessPlan.repayment_schedule[`${prefix}_y${yr}`] || ''}
                                onChange={e => updateRepay(`${prefix}_y${yr}`, e.target.value)}
                                className="text-sm h-8"
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Economic Growth */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Targeted Economic Growth</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="p-2 text-left font-medium text-slate-700 w-40">Category</th>
                        {['Present', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'].map(yr => (
                          <th key={yr} className="p-2 text-left font-medium text-slate-700">{yr}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['REVENUE / Sales', 'revenue', true],
                        ['Raw material / stock', 'exp_raw_material', false],
                        ['Labor / salary', 'exp_labor', false],
                        ['Rent', 'exp_rent', false],
                        ['Overhead & Misc', 'exp_overhead', false],
                        ['Repair & Maintenance', 'exp_maintenance', false],
                        ['Total Expenses', 'total_expenses', true],
                        ['PROFIT', 'profit', true],
                        ['(-) Credit & Dead stock', 'credit_deadstock', false],
                        ['(-) Qardan Repayment', 'qardan_repayment', false],
                        ['(-) Household expense', 'household_expense', false],
                        ['CASH SURPLUS', 'cash_surplus', true],
                      ].map(([label, key, bold]) => (
                        <tr key={key} className={bold ? 'bg-blue-50 font-semibold' : 'border-b border-slate-100'}>
                          <td className="p-2 text-slate-700">{label}</td>
                          {['present', 'year1', 'year2', 'year3', 'year4', 'year5'].map(yr => (
                            <td key={yr} className="p-1">
                              <Input
                                value={businessPlan.economic_growth[`${key}_${yr}`] || ''}
                                onChange={e => updateGrowth(`${key}_${yr}`, e.target.value)}
                                className="text-sm h-8"
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* STEP 5: Declaration */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-800">
                The applicant hereby confirms that the information provided in this form is correct and complete.
                Any amount granted by SHND will be utilized only for the business purpose as mentioned in this application.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldRow label="Applicant Name & Signature" value={declaration.applicant_name || personalDetails.name} onChange={v => updateDecl('applicant_name', v)} placeholder="Applicant name" />
                <FieldRow label="Counsellor Name & Signature" value={declaration.counsellor_name} onChange={v => updateDecl('counsellor_name', v)} placeholder="Counsellor name" />
                <FieldRow label="TR Committee Member" value={declaration.tr_member_name} onChange={v => updateDecl('tr_member_name', v)} placeholder="TR member name" />
              </div>
              <FieldRow label="Any Other Comments" value={declaration.comments} onChange={v => updateDecl('comments', v)} type="textarea" placeholder="Additional comments or remarks" />
            </div>
          )}

          {/* STEP 6: Review & Export */}
          {currentStep === 6 && (
            <div className="space-y-6">
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <h3 className="font-semibold text-emerald-800">Application Review</h3>
                </div>
                <p className="text-sm text-emerald-700">Review the summary below and export your application in the desired format.</p>
              </div>

              {/* Quick Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4 space-y-2">
                    <h4 className="font-medium text-slate-800">Applicant</h4>
                    <p className="text-sm text-slate-600">{personalDetails.name || '-'}</p>
                    <p className="text-sm text-slate-500">ITS: {personalDetails.its || '-'} | Age: {personalDetails.age || '-'}</p>
                    <p className="text-sm text-slate-500">Contact: {personalDetails.contact_number || '-'}</p>
                    <p className="text-sm text-slate-500">Occupation: {personalDetails.present_occupation || '-'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 space-y-2">
                    <h4 className="font-medium text-slate-800">Family</h4>
                    <p className="text-sm text-slate-600">Members: {familyDetails.family_members.length}</p>
                    <p className="text-sm text-slate-500">Structure: {familyDetails.family_structure || '-'}</p>
                    <p className="text-sm text-slate-500">Monthly Income: {familyDetails.income_expense.total_income_monthly || '-'}</p>
                    <p className="text-sm text-slate-500">Monthly Expense: {familyDetails.income_expense.total_expense_monthly || '-'}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Export buttons */}
              <Card>
                <CardContent className="p-5">
                  <h4 className="font-semibold text-slate-800 mb-4">Export Application</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Button
                      variant="outline" className="h-auto py-4 flex-col gap-2"
                      onClick={() => handleExport('pdf', 'full')}
                      disabled={!!exporting}
                      data-testid="export-pdf-full"
                    >
                      <FileText className="w-6 h-6 text-red-500" />
                      <span className="text-xs">Full Report PDF</span>
                      {exporting === 'pdf-full' && <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />}
                    </Button>
                    <Button
                      variant="outline" className="h-auto py-4 flex-col gap-2"
                      onClick={() => handleExport('word', 'full')}
                      disabled={!!exporting}
                      data-testid="export-word-full"
                    >
                      <File className="w-6 h-6 text-blue-500" />
                      <span className="text-xs">Full Report Word</span>
                      {exporting === 'word-full' && <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />}
                    </Button>
                    <Button
                      variant="outline" className="h-auto py-4 flex-col gap-2"
                      onClick={() => handleExport('pdf', 'summary')}
                      disabled={!!exporting}
                      data-testid="export-pdf-summary"
                    >
                      <FileText className="w-6 h-6 text-orange-500" />
                      <span className="text-xs">Summary PDF</span>
                      {exporting === 'pdf-summary' && <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />}
                    </Button>
                    <Button
                      variant="outline" className="h-auto py-4 flex-col gap-2"
                      onClick={() => handleExport('word', 'summary')}
                      disabled={!!exporting}
                      data-testid="export-word-summary"
                    >
                      <File className="w-6 h-6 text-teal-500" />
                      <span className="text-xs">Summary Word</span>
                      {exporting === 'word-summary' && <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />}
                    </Button>
                  </div>
                  <Button className="w-full mt-4" onClick={handleComplete} disabled={saving} data-testid="complete-btn">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Mark as Completed'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={goPrev} disabled={currentStep === 1} data-testid="prev-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Previous
        </Button>
        {currentStep < 6 && (
          <Button onClick={goNext} data-testid="next-btn">
            Next <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}
